import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import type { AppConfig } from '../../config';

import type { Logger } from '@seek/logger';
import {
  GET_COMPANY_PROFILE_AND_REVIEW_SUMMARY_QUERY,
  GET_COMPANY_SALARY_RANGE_QUERY,
} from './graphqlQueries';
import type { CompanyDetails } from './types';

export type CompanyDetailsService = ReturnType<
  typeof createCompanyDetailsService
>;

export const createCompanyDetailsService = ({
  config,
  client,
  logger,
}: {
  client: ApolloClient<NormalizedCacheObject>;
  config: AppConfig;
  logger: Logger;
}) => {
  const getCompanyDetails = async (
    companyId: string,
    isDraft = false,
  ): Promise<CompanyDetails> => {
    const variables = {
      companyDetailsId: companyId,
      draft: isDraft,
      locale: config.locale,
    };
    const response = await client.query<{
      companyDetails: CompanyDetails | null;
    }>({
      query: GET_COMPANY_PROFILE_AND_REVIEW_SUMMARY_QUERY,
      variables,
    });

    if (response?.error || response?.errors) {
      logger.error(
        { error: response.error ?? response.errors, variables },
        'Error calling getCompanyDetails',
      );
    }

    if (!response?.data?.companyDetails?.companyProfile) {
      throw new Error('Company profile not found for company ID', {
        ...variables,
        cause: {
          statusCode: 404,
          result: 'Could not find companyId from params',
        },
      });
    }

    return response.data.companyDetails;
  };

  const getCompanySalaryRange = async (
    companyId: string,
  ): Promise<{
    data: CompanyDetails['companySalaryRange'];
    error: Error | null;
  }> => {
    const variables = {
      companyDetailsId: companyId,
      locale: config.locale,
    };

    try {
      const response = await client.query<{
        companyDetails: CompanyDetails | null;
      }>({
        query: GET_COMPANY_SALARY_RANGE_QUERY,
        variables,
      });

      if (response?.error || response?.errors) {
        logger.error(
          { error: response.error ?? response.errors, variables },
          'Error calling getCompanySalaryRange',
        );
        return {
          data: null,
          error: new Error('GraphQL error query to getCompanySalaryRange'),
        };
      }

      return {
        data: response.data.companyDetails?.companySalaryRange ?? null,
        error: null,
      };
    } catch (error) {
      logger.error({ error, variables }, 'Error calling getCompanySalaryRange');
      return { data: null, error: error as Error };
    }
  };

  return {
    getCompanyDetails,
    getCompanySalaryRange,
  };
};
